import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ProgressCircle, ButtonSecondary, IconDownload } from '@americanexpress/dls-react';
import { useReadELetterRequest, useUpdateCustomerApplicationRequest } from '../../../utils/APIs';

const ViewedConfirmationSection = ({
  isViewedChecked,
  isEnabled,
  isLoading: isConfirmationLoading,
  hasError,
  onChange,
  intl,
}) => (
  <div style={{ textAlign: 'center', marginTop: '24px' }}>
    <label
      htmlFor="eletter-viewed-checkbox-input"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isEnabled && !isViewedChecked ? 'pointer' : 'default',
        opacity: isEnabled || isViewedChecked ? 1 : 0.5,
      }}
    >
      {isViewedChecked && !hasError ? (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            marginRight: '8px',
            backgroundColor: '#00a651',
            borderRadius: '50%',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          data-testid="eletter-viewed-success-checkmark"
        >
          ✓
        </span>
      ) : (
        <input
          id="eletter-viewed-checkbox-input"
          type="checkbox"
          checked={isViewedChecked}
          onChange={onChange}
          disabled={!isEnabled || isViewedChecked || isConfirmationLoading}
          style={{ marginRight: '8px', cursor: 'pointer' }}
          data-testid="eletter-viewed-checkbox"
        />
      )}
      <span className="body-2">
        {intl.formatMessage({
          id: 'eletter.viewed.checkbox.label',
          defaultMessage: 'I have viewed this ELetter',
        })}
      </span>
    </label>
    {!isEnabled && !isViewedChecked && (
      <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
        {intl.formatMessage({
          id: 'eletter.viewed.checkbox.waiting',
          defaultMessage: 'Please wait to confirm you have viewed the letter...',
        })}
      </p>
    )}
    {isViewedChecked && !hasError && (
      <p style={{ marginTop: '8px', fontSize: '12px', color: '#00a651', fontWeight: '500' }}>
        {intl.formatMessage({
          id: 'eletter.viewed.checkbox.success',
          defaultMessage: 'Thank you for confirming you have viewed this letter.',
        })}
      </p>
    )}
    {hasError && (
      <p
        className="body-2 dls-color-warning"
        role="alert"
        style={{ marginTop: '8px' }}
      >
        {intl.formatMessage({
          id: 'eletter.viewed.checkbox.error',
          defaultMessage: 'We were unable to record that you viewed this letter. Please try again.',
        })}
      </p>
    )}
  </div>
);

ViewedConfirmationSection.propTypes = {
  isViewedChecked: PropTypes.bool.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  intl: PropTypes.shape({ formatMessage: PropTypes.func.isRequired }).isRequired,
};

const ViewELetter = ({ onExit, pcn, authorizationToken }) => {
  const intl = useIntl();
  const [pdfSrc, setPdfSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessError, setBusinessError] = useState(false);
  const [serviceError, setServiceError] = useState(false);
  const [pdfDisplayFailed, setPdfDisplayFailed] = useState(false);
  const [isViewedChecked, setIsViewedChecked] = useState(false);
  const [viewedCheckboxEnabled, setViewedCheckboxEnabled] = useState(false);
  const [viewedCheckboxLoading, setViewedCheckboxLoading] = useState(false);
  const [viewedCheckboxError, setViewedCheckboxError] = useState(false);
  const [useDownloadFallback] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    // Mobile browsers and desktop Safari can't reliably render blob:// PDFs in an iframe.
    const isMobile = /mobi|android|iphone|ipad|ipod/i.test(ua);
    const isDesktopSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    return isMobile || isDesktopSafari;
  });
  const iframeRef = useRef(null);

  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current) return;
    try {
      const doc = iframeRef.current.contentDocument;
      // Null contentDocument means the native PDF viewer took control — rendering succeeded.
      // A non-null but empty document means the browser could not render the PDF inline.
      if (doc !== null && (!doc.body || doc.body.childNodes.length === 0)) {
        setPdfDisplayFailed(true);
      }
    } catch {
      // contentDocument access threw — native PDF viewer is active, all good.
    }
  }, []);

  const { runReadELetterRequest } = useReadELetterRequest(authorizationToken);
  const { runUpdateCustomerApplicationRequest } = useUpdateCustomerApplicationRequest(
    pcn,
    'VIEWED',
    'ELECTRONIC_COMMUNICATION_VIEWED',
  );
  const isInlinePdfDisplay = pdfSrc && !useDownloadFallback && !pdfDisplayFailed;

  useEffect(() => {
    if (!authorizationToken) {
      setIsLoading(false);
      setServiceError(true);
      return undefined;
    }

    let blobUrl = null;

    const fetchELetter = async () => {
      setIsLoading(true);
      setBusinessError(false);
      setServiceError(false);

      try {
        const { data, error: apiError } = await runReadELetterRequest();

        if (apiError) {
          setServiceError(true);
        } else {
          const base64 = data?.body?.documentBinary;
          if (base64) {
            const binary = atob(base64.replace(/\s+/g, ''));
            const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: 'application/pdf' });
            blobUrl = URL.createObjectURL(blob);
            setPdfSrc(blobUrl);
          } else {
            setBusinessError(true);
          }
        }
      } catch {
        setServiceError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchELetter();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [authorizationToken]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-fetch when token changes

  // Enable checkbox after 5 seconds only when PDF is displayed inline.
  useEffect(() => {
    if (!isInlinePdfDisplay || isLoading) return undefined;

    const timer = setTimeout(() => {
      setViewedCheckboxEnabled(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isInlinePdfDisplay, isLoading]);

  const handleViewedCheckboxChange = useCallback(async (e) => {
    const isChecked = e.target.checked;
    setIsViewedChecked(isChecked);
    setViewedCheckboxError(false);

    if (isChecked) {
      setViewedCheckboxLoading(true);
      try {
        const { error: apiError } = await runUpdateCustomerApplicationRequest();
        if (apiError) {
          setViewedCheckboxError(true);
          setIsViewedChecked(false);
        }
      } catch {
        setViewedCheckboxError(true);
        setIsViewedChecked(false);
      } finally {
        setViewedCheckboxLoading(false);
      }
    }
  }, [runUpdateCustomerApplicationRequest]);

  const handleFallbackDownloadClick = useCallback(async () => {
    if (isViewedChecked || viewedCheckboxLoading) return;

    setViewedCheckboxError(false);
    setViewedCheckboxLoading(true);

    try {
      const { error: apiError } = await runUpdateCustomerApplicationRequest();
      if (apiError) {
        setViewedCheckboxError(true);
      } else {
        setIsViewedChecked(true);
      }
    } catch {
      setViewedCheckboxError(true);
    } finally {
      setViewedCheckboxLoading(false);
    }
  }, [isViewedChecked, viewedCheckboxLoading, runUpdateCustomerApplicationRequest]);

  const handleButtonDownload = useCallback(() => {
    window.open(pdfSrc, '_blank', 'noopener,noreferrer');
    handleFallbackDownloadClick();
  }, [pdfSrc, handleFallbackDownloadClick]);

  if (!global.BROWSER) return null;

  return (
    <div>
      <div className="pad-3-t">
        <h2 className="heading-5 pad-2-b" style={{ textAlign: 'center' }}>
          {intl.formatMessage({
            id: 'eletter.heading',
            defaultMessage: 'Your Decision Letter',
          })}
        </h2>

        {isLoading && (
          <div className="pad-3-t" style={{ textAlign: 'center' }}>
            <ProgressCircle
              determinate={false}
              label={intl.formatMessage({
                id: 'eletter.loading',
                defaultMessage:
                  'Your decision letter is being retrieved. Please wait.',
              })}
            />
          </div>
        )}

        {!isLoading && businessError && (
          <p className="mar-1-t dls-color-warning" role="alert">
            {intl.formatMessage({
              id: 'eletter.error.business',
              defaultMessage:
                'We could not retrieve your decision letter. Please try again.',
            })}
          </p>
        )}

        {!isLoading && serviceError && (
          <p className="mar-1-t dls-color-warning" role="alert">
            {intl.formatMessage({
              id: 'eletter.error.service',
              defaultMessage:
                'We are currently experiencing technical difficulties. Please try again later.',
            })}
          </p>
        )}

        {!isLoading && isInlinePdfDisplay && (
          <ViewedConfirmationSection
            isViewedChecked={isViewedChecked}
            isEnabled={viewedCheckboxEnabled}
            isLoading={viewedCheckboxLoading}
            hasError={viewedCheckboxError}
            onChange={handleViewedCheckboxChange}
            intl={intl}
          />
        )}

        {!isLoading && pdfSrc && (
          useDownloadFallback ? (
            <div className="pad-2-t" style={{ textAlign: 'center' }}>
              <ButtonSecondary
                icon={<IconDownload />}
                onClick={handleButtonDownload}
              >
                {intl.formatMessage({
                  id: 'eletter.pdf.download',
                  defaultMessage: 'Download',
                })}
              </ButtonSecondary>
              {viewedCheckboxError && (
                <p className="body-2 dls-color-warning" role="alert" style={{ marginTop: '8px' }}>
                  {intl.formatMessage({
                    id: 'eletter.viewed.checkbox.error',
                    defaultMessage: 'We were unable to record that you viewed this letter. Please try again.',
                  })}
                </p>
              )}
            </div>
          ) : pdfDisplayFailed ? (
            <div className="pad-2-t" style={{ textAlign: 'center' }}>
              <p className="body-2" role="alert">
                {intl.formatMessage({
                  id: 'eletter.pdf.display.failed',
                  defaultMessage: 'Browser cannot load file.',
                })}
              </p>
              <ButtonSecondary
                icon={<IconDownload />}
                onClick={handleButtonDownload}
              >
                {intl.formatMessage({
                  id: 'eletter.pdf.download',
                  defaultMessage: 'Download',
                })}
              </ButtonSecondary>
              {viewedCheckboxError && (
                <p className="body-2 dls-color-warning" role="alert" style={{ marginTop: '8px' }}>
                  {intl.formatMessage({
                    id: 'eletter.viewed.checkbox.error',
                    defaultMessage: 'We were unable to record that you viewed this letter. Please try again.',
                  })}
                </p>
              )}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={pdfSrc}
              title={intl.formatMessage({
                id: 'eletter.iframe.title',
                defaultMessage: 'Your Decision Letter PDF',
              })}
              onLoad={handleIframeLoad}
              style={{ width: '100%', height: '800px', border: 'none' }}
            />
          )
        )}
      </div>

      <div className="pad-3-t" style={{ textAlign: 'center' }}>
        <button
          type="button"
          onClick={onExit}
          style={{
            background: 'none',
            border: 'none',
            color: 'blue',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {intl.formatMessage({
            id: 'eletter.back',
            defaultMessage: 'Back to results',
          })}
        </button>
      </div>
    </div>
  );
};

ViewELetter.defaultProps = {
  pcn: null,
  authorizationToken: null,
};

ViewELetter.propTypes = {
  pcn: PropTypes.string,
  authorizationToken: PropTypes.string,
  onExit: PropTypes.func.isRequired,
};

export default ViewELetter;
